import React, { useRef, useEffect, useState } from 'react';
import { __, sprintf } from '@wordpress/i18n';
import { classNames } from 'primereact/utils';
import { head, isEmpty } from 'ramda';
import { klona } from 'klona';

// store
import { storeGet, storeSet } from '../../store';

// api
import CompanyService from '../../service/company-service';

// tools
import set404FromErrorResponse from '../../helpers/set404FromErrorResponse';
import { isCodiceFiscale, isEmail, isEmailPEC, isPIVA } from '../../helpers/validators';

// components
import LogoIcon from '../../icons/LogoIcon';
import { Messages } from 'primereact/messages';
import FormField from '../../components/FormField';
import { useForm } from 'react-hook-form';
import { Button } from 'primereact/button';
import { useDebounce } from 'primereact/hooks';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BlockingOverlay from '../../components/BlockingOverlay';

const FirstAccess = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    let invitationData = searchParams.get('data');
    if (invitationData) {
        invitationData = atob(invitationData);
        const searchParams = new URLSearchParams(invitationData);
        invitationData = Object.fromEntries(searchParams)
    }
    const [loading, setLoading] = useState(false);
    const [, debouncedPivaValue, setInputPiva] = useDebounce('', 1000);
    const [vatCheckResponse, setVatCheckResponse] = useState({});
    const [isVatValid, setIsVatValid] = useState(true);
    const [formStep, setFormStep] = useState(1);
    const errorMsgs = useRef(null);
    const {
        control,
        handleSubmit,
        watch,
        trigger,
        formState: { errors },
        setValue
    } = useForm({ 
        defaultValues: {
            lookUpDataIds: [],
            // L'email di contatto parte da quella di registrazione: il fornitore puo' cambiarla.
            contactEmail: storeGet('userData')?.email || ''
        }, 
        mode: 'onChange' 
    });
    const isPiva = watch('vatNumber');

    /*const setEmptyValues = () => {
        const formData = {
            cap: '',
            pec: '',
            email: '',
            city: '',
            codiceFiscale: '',
            address: '',
            companyName: ''
        }
        setVatCheckResponse({});
        Object.keys(formData).map(k => setValue(k, formData[k]));
    }*/

    const onSubmit = (formData) => {
        errorMsgs.current.clear();
        storeSet('setAsyncRequest');
        const submitData = {
            ...formData,
            email: formData.contactEmail,
            vatCheckResponse
        }

        CompanyService.createCompany(submitData, updateCallback, updateError);
    };

    const updateCallback = (data) => {
        if (data.status === 'SUCCESS') {
            const company = klona(data.data);
            const companies = storeGet('companies');
            const existingCompany = head(companies.filter(o => o.id === company.id));
            let newCompanies = [];

            if (existingCompany) {
                newCompanies = companies.map(o => o.id === company.id ? company : o)
            } else {
                newCompanies = [...companies, company];
                storeSet('chosenCompanyId', company.id);
            }

            storeSet('companies', newCompanies);

            if (company.isLegalRepresentant) {
                navigate('/');
            } else {
                navigate('/profilo-aziendale');
            }

        }
        storeSet('unsetAsyncRequest');
    }

    const updateError = (data) => {
        set404FromErrorResponse(data);
        storeSet('unsetAsyncRequest');
    }

    const onSubmitStepOne = () => {
        errorMsgs.current.clear();
        setFormStep(2);
    }

    const checkVatNumber = (value) => {
        errorMsgs.current.clear();
        const isValid = isPIVA(value);

        if (isValid) {
            setLoading(true);
            CompanyService.checkVat(checkVatCallback, errCheckVatCallback, [
                ['vatNumber', value]
            ])
        }
    }

    const checkVatCallback = (data) => {
        if (data.status === 'SUCCESS') {
            if (!data.data.valid) {
                setIsVatValid(false);
                errorMsgs.current.show([
                    {
                        sticky: true, severity: 'error', summary: '',
                        detail: sprintf(__('%s', 'htr'), data.data.message),
                        closable: true
                    }
                ]);
            } else if (data.data.alreadyExistsInDb) {
                setIsVatValid(false);
                errorMsgs.current.show([
                    {
                        sticky: true, severity: 'error', summary: '',
                        detail: __('Non è possibile utilizzare questa Partita IVA perché risulta già associata a un soggetto registrato.', 'htr'),
                        closable: true
                    }
                ]);
            } else {
                setIsVatValid(true);
            }

            const version = data.data.version;
            const resp = data.data?.vatCheckResponse?.data;
            if (resp && !isEmpty(resp)) {
                let formData = {};

                if (version === 'V2') {
                    const firstItem = resp[0];
                    const {
                        taxCode, vatCode, address, companyName, pec
                    } = firstItem;
                    const { streetName, zipCode, town } = address?.registeredOffice;

                    formData = {
                        cap: zipCode,
                        pec,
                        city: town,
                        codiceFiscale: taxCode ? taxCode : vatCode,
                        address: streetName,
                        vatNumber: vatCode,
                        companyName
                    }
                } else {
                    const {
                        cap, cf, denominazione, piva, indirizzo, comune, dettaglio: { pec }
                    } = resp;

                    formData = {
                        cap,
                        pec,
                        city: comune,
                        codiceFiscale: cf ? cf : piva,
                        address: indirizzo,
                        vatNumber: piva,
                        companyName: denominazione
                    }
                }
                Object.keys(formData).map(k => setValue(k, formData[k]));
                setVatCheckResponse(data.data.vatCheckResponse);
            }
        }
        trigger();
        setLoading(false);
    }

    const errCheckVatCallback = (resp) => {
        set404FromErrorResponse(resp);
        setLoading(false);
    }

    useEffect(() => {
        setInputPiva(isPiva);
    }, [isPiva]);

    useEffect(() => {
        checkVatNumber(debouncedPivaValue);
    }, [debouncedPivaValue]);

    useEffect(() => {
        storeSet('isRedirectedOnceNoCompany', true);
    }, []);

    return (
        <div className={classNames(['appPage', 'appPageNewCompany'])}>
            <div className="appPageNewCompany__wrapper">
                <LogoIcon/>

                {formStep === 1
                    ? <>
                        <h1>{__('Benvenuto!', 'htr')}<br/>{__('Registra la tua prima azienda!', 'htr')}</h1>
                        <p className="description">{__('Per registrare la tua azienda come Fornitore inserisci la ragione sociale e la partita I.V.A.', 'htr')}</p>
                    </> : null}

                {formStep === 2
                    ? <>
                        <h1>{__('Completa Anagrafica Azienda', 'htr')}</h1>
                        <p className="description">{__('Per favore, verifica i dati precompilati e completa le informazioni mancanti.', 'htr')}</p>
                    </> : null}

                <Messages ref={errorMsgs}/>

                <div className="appPage__spacer"></div>

                {formStep === 1
                    ? <form className={['appForm', 'addCompanyStepOne'].join(' ')}
                            onSubmit={handleSubmit(onSubmitStepOne)}>
                        <BlockingOverlay shouldDisplay={loading}/>

                        <div className="appForm__cols">
                            <FormField
                                type="textinput"
                                fieldName="companyName"
                                label={__('Ragione sociale', 'htr')}
                                control={control}
                                errors={errors}
                                config={{ required: __('È obbligatorio', 'htr') }}
                                placeholder="Il nome della tua azienda"/>
                        </div>

                        <div className="appForm__cols">
                            <FormField
                                type="textinput"
                                fieldName="vatNumber"
                                label={__('Partita IVA', 'htr')}
                                control={control}
                                errors={errors}
                                config={{ required: __('È obbligatorio', 'htr') }}
                                placeholder="XXXXXXXXXXX"/>
                        </div>

                        <Button
                            label={__('Continua', 'htr')}
                            icon="pi pi-arrow-right" iconPos="right"
                            disabled={loading || !isVatValid}/>
                    </form> : null}

                {formStep === 2
                    ? <form id="companyForm" className="appForm" onSubmit={handleSubmit(onSubmit)}>
                        <BlockingOverlay shouldDisplay={loading}/>
                        <div className="appPageSection">
                            <div className="appForm__cols">
                                <FormField
                                    type="textinput"
                                    /*disabled={true}*/
                                    fieldName="companyName"
                                    label={__('Ragione Sociale', 'htr')}
                                    control={control}
                                    errors={errors}
                                    config={{ required: __('È obbligatorio', 'htr') }}
                                    placeholder="Nome di azienda"
                                />
                            </div>

                            <div className="appForm__cols">
                                <FormField
                                    type="textinput"
                                    fieldName="vatNumber"
                                    label={__('P.IVA', 'htr')}
                                    control={control}
                                    errors={errors}
                                    config={{
                                        required: __('È obbligatorio', 'htr'),
                                        validate: {
                                            isPIVA
                                        }
                                    }}
                                />

                                <FormField
                                    type="textinput"
                                    fieldName="codiceFiscale"
                                    label={__('Codice fiscale', 'htr')}
                                    control={control}
                                    errors={errors}
                                    config={{
                                        required: __('È obbligatorio', 'htr'),
                                        validate: {
                                            validCF: (value) => isCodiceFiscale(value) || isPIVA(value)
                                        }
                                    }}
                                />
                            </div>

                            <div className="appForm__cols">
                                <FormField
                                    type="textinput"
                                    /*disabled={true}*/
                                    fieldName="pec"
                                    label={__('PEC', 'htr')}
                                    infoText={__('Solo dato anagrafico: il portale non invia comunicazioni alla PEC.', 'htr')}
                                    control={control}
                                    errors={errors}
                                    config={{
                                        validate: {
                                            isEmailPEC: (v) => !v || isEmailPEC(v)
                                        }
                                    }}
                                />
                            </div>

                            <div className="appForm__cols">
                                <FormField
                                    type="textinput"
                                    /*disabled={true}*/
                                    fieldName="address"
                                    label={__('Indirizzo', 'htr')}
                                    control={control}
                                    errors={errors}
                                    config={{ required: __('È obbligatorio', 'htr') }}
                                />

                                <FormField
                                    type="textinput"
                                    /*disabled={true}*/
                                    fieldName="city"
                                    label={__('Città', 'htr')}
                                    control={control}
                                    errors={errors}
                                    config={{ required: __('È obbligatorio', 'htr') }}
                                />

                                <FormField
                                    type="textinput"
                                    /*disabled={true}*/
                                    fieldName="cap"
                                    label={__('CAP', 'htr')}
                                    control={control}
                                    errors={errors}
                                    config={{ required: __('È obbligatorio', 'htr') }}
                                />
                            </div>

                            <div className="appForm__cols">
                                <FormField
                                    type="textinput"
                                    fieldName="contactName"
                                    label={__('Nome del referente', 'htr')}
                                    control={control}
                                    errors={errors}
                                    config={{ required: __('È obbligatorio', 'htr') }}
                                />

                                <FormField
                                    type="textinput"
                                    fieldName="contactEmail"
                                    label={__('Email di contatto', 'htr')}
                                    infoText={__('A questo indirizzo arrivano tutte le comunicazioni del portale: richieste di integrazioni, esiti, promemoria.', 'htr')}
                                    control={control}
                                    errors={errors}
                                    config={{
                                        required: __('È obbligatorio', 'htr'),
                                        validate: {
                                            isEmail: isEmail
                                        }
                                    }}
                                />
                            </div>

                            <div className="appForm__cols">
                                <FormField
                                    type="chips"
                                    fieldName="lookUpDataIds"
                                    label={__('Seleziona le categorie', 'htr')}
                                    control={control}
                                    errors={errors}
                                    multiple={true}
                                    config={{
                                        required: true,
                                        validate: value => (Array.isArray(value) ? value.length > 0 : value > 0) || __('Questo campo è obbligatorio', 'htr')
                                    }}
                                    lookupType="AIMED_TO"
                                />
                            </div>
                        </div>

                        {/*<div className="appPageSection">
                            <FormField
                                type="switch"
                                fieldName="isLegalRepresentant"
                                label={__('Sei il Rappresentante Legale dell’azienda?', 'htr')}
                                control={control}
                                errors={errors}
                                onLabel={__('Si', 'htr')}
                                offLabel={__('No', 'htr')}
                            />
                        </div>*/}

                        <Button
                            label={__('Invia', 'htr')}
                            disabled={loading || !isVatValid}/>
                    </form> : null}
            </div>
        </div>
    )
}

export default FirstAccess;